import { FunctionComponent } from 'react'
import { Skeleton } from '@/global/components/Skeleton'

/*
 * Espelha o drawer carregado: cabecalho com imagem quadrada ao lado do titulo e
 * das tags, e secoes separadas por linha. O especialista mostra tres tags e o
 * bloco de avaliacao; a rede mostra so o CNPJ e menos secoes.
 */
type ProviderKind = 'specialist' | 'establishment'

interface ProviderDrawerSkeletonProps {
  kind?: ProviderKind
}

const TAG_WIDTHS: Record<ProviderKind, string[]> = {
  specialist: ['w-[90px]', 'w-[122px]', 'w-[105px]'],
  establishment: ['w-[180px]']
}

const SECTIONS: Record<ProviderKind, number> = {
  specialist: 4,
  establishment: 2
}

const RATING_CARDS = [0, 1, 2]

const ProviderDrawerSkeleton: FunctionComponent<
  ProviderDrawerSkeletonProps
> = ({ kind = 'specialist' }) => {
  const isSpecialist = kind === 'specialist'

  return (
    <div className='flex flex-col lg:px-gl-spacing-10 pt-[96px]'>
      <div className='mb-gl-spacing-06 flex items-center gap-gl-spacing-06 px-gl-spacing-06 lg:px-0'>
        {/* Sem `variant`: o raio do quadrado da foto e `sm`, que nenhum deles da. */}
        <Skeleton
          className='shrink-0 rounded-gl-border-radius-sm lg:h-20 lg:w-20'
          height='h-[72px]'
          width='w-[72px]'
        />

        <div className='flex min-w-0 flex-col gap-gl-spacing-03'>
          <Skeleton
            variant='rectangular'
            height='h-[32px]'
            width='w-[220px]'
          />

          <div className='flex flex-wrap gap-gl-spacing-02'>
            {/*
             * `rounded` e nao `circular`: o raio do `circular` e 50%, que numa
             * caixa larga e baixa vira elipse. O `rounded` usa 50px, clampado
             * pela altura, entao sai a pilula da tag.
             */}
            {TAG_WIDTHS[kind].map(width => (
              <Skeleton
                key={width}
                variant='rounded'
                height='h-[23px]'
                width={width}
              />
            ))}
          </div>
        </div>
      </div>

      {isSpecialist ? (
        <div className='flex flex-col gap-gl-spacing-08 px-gl-spacing-06 lg:px-0'>
          <div className='flex flex-col gap-gl-spacing-02'>
            <Skeleton
              variant='rectangular'
              height='h-[16px]'
              width='w-full'
            />

            <Skeleton
              variant='rectangular'
              height='h-[16px]'
              width='w-full'
            />

            <Skeleton
              variant='rectangular'
              height='h-[16px]'
              width='w-[60%]'
            />
          </div>

          <div className='grid grid-cols-3 gap-gl-spacing-02'>
            {RATING_CARDS.map(card => (
              <Skeleton
                key={card}
                className='rounded-gl-border-radius-sm'
                height='h-[128px]'
                width='w-full'
              />
            ))}
          </div>
        </div>
      ) : null}

      {Array.from({ length: SECTIONS[kind] }, (_, section) => (
        <div
          key={section}
          className='flex flex-col gap-gl-spacing-04 border-t border-sys-color-stroke-idle px-gl-spacing-06 py-gl-spacing-08 lg:px-0'
        >
          <Skeleton
            variant='rectangular'
            height='h-[20px]'
            width='w-[140px]'
          />

          <Skeleton
            variant='rectangular'
            height='h-[16px]'
            width='w-full'
          />

          <Skeleton
            variant='rectangular'
            height='h-[16px]'
            width='w-[70%]'
          />
        </div>
      ))}
    </div>
  )
}

export type { ProviderDrawerSkeletonProps, ProviderKind }

export { ProviderDrawerSkeleton }
